import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Rating,
  IconButton, CircularProgress, Alert,
  FormControl, Select, MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InstagramIcon from '@mui/icons-material/Instagram';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import { recordCall, canRefreshPhoto, PHOTO_EXPIRY_MS } from '../../utils/apiUsage';

const CUISINES = [
  'Japanese', 'Chinese', 'Korean', 'Thai', 'Vietnamese', 'Indian',
  'Italian', 'French', 'American', 'Mexican', 'Mediterranean',
  'Middle Eastern', 'Hawker / Local', 'Cafe', 'Bakery', 'Bar & Drinks', 'Other',
];

const PRIORITIES = [
  { value: 'mustgo', label: 'Must Go', color: '#ef5350' },
  { value: 'luxury', label: 'Luxury', color: '#ffca28' },
  { value: 'healthy', label: 'Healthy', color: '#66bb6a' },
];


export default function PlaceFormDialog({ open, place, prefill, onClose, onSave, mapsLoaded }) {
  const isEdit = !!place;
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [placeId, setPlaceId] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [photoURL, setPhotoURL] = useState(null);
  const [photoGeneratedAt, setPhotoGeneratedAt] = useState(null);
  const [cuisine, setCuisine] = useState('');
  const [category, setCategory] = useState('');
  const [instagram, setInstagram] = useState('');
  const [instagramCaption, setInstagramCaption] = useState('');
  const [remarks, setRemarks] = useState('');
  const [visited, setVisited] = useState(false);
  const [visitedRating, setVisitedRating] = useState(null);
  const [visitedNotes, setVisitedNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [captionOpen, setCaptionOpen] = useState(false);

  // Initial load on open. Deliberately omit `prefill` from deps — the unfurl
  // (if it ever lands) can arrive after the dialog is open, and we don't want
  // to clobber whatever the user has typed. Late updates handled below.
  useEffect(() => {
    if (!open) return;
    if (place) {
      setName(place.name || '');
      setAddress(place.address || '');
      setPlaceId(place.placeId || null);
      setLatitude(place.latitude || null);
      setLongitude(place.longitude || null);
      setPhotoURL(place.photoURL || null);
      setPhotoGeneratedAt(place.photoGeneratedAt || null);
      setCuisine(place.cuisine || '');
      setCategory(place.category || '');
      setInstagram(place.instagram || '');
      setInstagramCaption(place.instagramCaption || '');
      setRemarks(place.remarks || '');
      setVisited(place.visited || false);
      setVisitedRating(place.visitedRating || null);
      setVisitedNotes(place.visitedNotes || '');
    } else {
      setName(prefill?.name || '');
      setAddress(''); setPlaceId(null); setLatitude(null); setLongitude(null);
      setPhotoURL(prefill?.photoURL || null);
      setPhotoGeneratedAt(null);
      setCuisine(''); setCategory('');
      setInstagram(prefill?.instagram || '');
      setInstagramCaption(prefill?.instagramCaption || '');
      setRemarks(prefill?.remarks || '');
      setVisited(false); setVisitedRating(null); setVisitedNotes('');
    }
    setCaptionOpen(false);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, place]);

  useEffect(() => {
    if (!open || place || !prefill) return;
    if (prefill.name) setName(prev => (prev && prev.trim() ? prev : prefill.name));
    if (prefill.instagramCaption) {
      setInstagramCaption(prev => (prev && prev.trim() ? prev : prefill.instagramCaption));
    }
    // IG cover image fills in late too — only use it if the user hasn't
    // already picked a Google Place (which supplies a better, non-expiring URL).
    if (prefill.photoURL) {
      setPhotoURL(prev => (prev || prefill.photoURL));
    }
  }, [open, place, prefill]);

  useEffect(() => {
    if (!open || !mapsLoaded || !window.google?.maps?.places) return;
    let ac = null;
    const timeout = setTimeout(() => {
      if (!inputRef.current) return;
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['establishment'],
        componentRestrictions: { country: 'sg' },
        fields: ['name', 'place_id', 'geometry', 'formatted_address', 'photos'],
        sessionToken: sessionTokenRef.current,
      });
      autocompleteRef.current = ac;
      ac.addListener('place_changed', () => {
        const p = ac.getPlace();
        if (!p.geometry) return;
        // A session that resolves to a pick is the billable event
        recordCall('autocompleteSession');
        setName(p.name || '');
        setAddress(p.formatted_address || '');
        setPlaceId(p.place_id || null);
        setLatitude(p.geometry.location.lat());
        setLongitude(p.geometry.location.lng());
        if (p.photos && p.photos.length > 0) {
          const url = p.photos[0].getUrl({ maxWidth: 400, maxHeight: 400 });
          setPhotoURL(url);
          setPhotoGeneratedAt(Date.now());
        }
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      });
    }, 150);
    return () => {
      clearTimeout(timeout);
      if (ac) window.google.maps.event.clearInstanceListeners(ac);
      autocompleteRef.current = null;
      document.querySelectorAll('.pac-container').forEach(el => el.remove());
    };
  }, [open, mapsLoaded]);

  useEffect(() => {
    if (!placeId || !photoGeneratedAt) return;
    const isExpired = Date.now() - photoGeneratedAt > PHOTO_EXPIRY_MS;
    if (!isExpired) return;
    if (!window.google?.maps?.places) return;
    if (!canRefreshPhoto()) return;
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    recordCall('placeDetails');
    service.getDetails({ placeId, fields: ['photos'] }, (result, status) => {
      if (status === 'OK' && result.photos?.length) {
        const url = result.photos[0].getUrl({ maxWidth: 400 });
        setPhotoURL(url);
        setPhotoGeneratedAt(Date.now());
      }
    });
  }, [placeId, photoGeneratedAt]);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await onSave({
        name: name.trim(), address, placeId, latitude, longitude,
        photoURL, photoGeneratedAt, cuisine, category,
        instagram: instagram.trim(),
        instagramCaption: instagramCaption.trim(),
        remarks: remarks.trim(),
        visited, visitedRating: visited ? visitedRating : null,
        visitedNotes: visited ? visitedNotes.trim() : '',
      });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const hostLabel = (() => {
    if (!instagram) return '';
    try {
      const u = new URL(instagram);
      return u.pathname.replace(/\/+$/, '').replace(/^\/+/, '') || u.hostname;
    } catch (_) { return instagram.replace(/^https?:\/\//, '').slice(0, 36); }
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>

      {/* Editorial header: italic Fraunces title, no close icon chip. */}
      <Box sx={{
        display: 'flex', alignItems: 'baseline',
        px: 2.5, pt: 2.25, pb: 0.75,
      }}>
        <Typography sx={{
          flex: 1,
          fontFamily: '"Fraunces", serif', fontStyle: 'italic',
          fontSize: '1.3rem', fontWeight: 500,
          letterSpacing: '-0.02em',
        }}>
          {isEdit ? 'Edit' : 'Add a place'}
        </Typography>
        <IconButton size="small" onClick={onClose}
          sx={{ color: 'text.disabled', mr: -1, '&:hover': { color: 'text.secondary' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: 1, px: 2.5, pb: 2 }}>
        {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}

        {/* Photo preview — only when we actually have one */}
        {photoURL && (
          <Box sx={{ width: '100%', height: 148, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            <img src={photoURL} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setPhotoURL(null)} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(11,10,12,0.8) 0%, transparent 55%)' }} />
            {name && (
              <Typography sx={{
                position: 'absolute', bottom: 12, left: 14, right: 14,
                color: '#f4efe9',
                fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: '1.1rem',
                letterSpacing: '-0.015em',
                textShadow: '0 1px 6px rgba(0,0,0,0.8)',
              }}>
                {name}
              </Typography>
            )}
          </Box>
        )}

        {/* Hero: single search input, underline-only (not a pill box) */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: '1px solid rgba(244,239,233,0.1)',
          pb: 0.25,
          transition: 'border-color .18s',
          '&:focus-within': { borderColor: 'rgba(227,105,80,0.6)' },
        }}>
          <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
          <TextField
            inputRef={inputRef}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            variant="standard"
            autoFocus={!isEdit && !!prefill?.instagram}
            placeholder={isEdit ? 'Place name' : 'Search a place, or type a name'}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '1rem', fontWeight: 500, py: 0.75 },
            }}
            inputProps={{ autoComplete: 'off' }}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
          />
        </Box>

        {/* Secondary context: address + IG, as quiet text — not chips */}
        {(address || instagram) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: -0.75 }}>
            {address && (
              <Typography sx={{
                fontSize: '0.74rem', color: 'text.disabled',
                letterSpacing: '-0.003em',
              }}>
                {address}
              </Typography>
            )}
            {instagram && (
              <Box
                component="a"
                href={instagram}
                target="_blank"
                rel="noopener"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.3,
                  textDecoration: 'none', color: 'text.secondary',
                  fontSize: '0.72rem', fontWeight: 500,
                  '&:hover': { color: 'text.primary' },
                  width: 'fit-content',
                }}
              >
                <InstagramIcon sx={{ fontSize: 11 }} />
                <Box component="span" sx={{
                  maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  borderBottom: '1px dashed currentColor',
                }}>
                  {hostLabel}
                </Box>
                <LaunchIcon sx={{ fontSize: 10, opacity: 0.7 }} />
              </Box>
            )}
          </Box>
        )}

        {/* Priority — flat text toggles with a leading dot, not chips */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', mt: 0.25 }}>
          <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
            Priority
          </Typography>
          {PRIORITIES.map(p => {
            const active = category === p.value;
            return (
              <Box
                key={p.value}
                onClick={() => setCategory(active ? '' : p.value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer',
                  fontSize: '0.78rem', fontWeight: active ? 600 : 500,
                  color: active ? 'text.primary' : 'text.disabled',
                  letterSpacing: '-0.003em',
                  '&:hover': { color: active ? 'text.primary' : 'text.secondary' },
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.color }} />
                {p.label}
              </Box>
            );
          })}
        </Box>

        {/* Cuisine — single row, minimal label + inline select trigger */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
            Cuisine
          </Typography>
          <FormControl size="small" variant="standard" sx={{ flex: 1 }}>
            <Select
              value={cuisine}
              onChange={e => setCuisine(e.target.value)}
              displayEmpty
              variant="standard"
              disableUnderline
              IconComponent={() => null}
              renderValue={v => (
                <Typography sx={{
                  fontSize: '0.82rem',
                  fontWeight: v ? 500 : 400,
                  color: v ? 'text.primary' : 'text.disabled',
                }}>
                  {v || 'Pick one'}
                </Typography>
              )}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {CUISINES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* Notes — underline-only input, no boxy field */}
        <Box>
          <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.62rem', mb: 0.3, display: 'block' }}>
            Notes
          </Typography>
          <TextField
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            fullWidth multiline minRows={1} maxRows={5}
            variant="standard"
            placeholder="What to order, vibe, who recommended…"
            InputProps={{
              sx: {
                fontSize: '0.84rem', letterSpacing: '-0.003em', lineHeight: 1.5,
                '&:before': { borderBottom: '1px solid rgba(244,239,233,0.08) !important' },
                '&:after': { borderBottom: '1px solid rgba(227,105,80,0.6) !important' },
              },
            }}
          />
        </Box>

        {/* Instagram caption — minimal expandable row when present */}
        {instagramCaption && (
          <Box>
            <Box
              onClick={() => setCaptionOpen(v => !v)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                pb: 0.5, borderBottom: '1px solid rgba(244,239,233,0.08)',
                '&:hover > *': { color: 'text.secondary' },
              }}
            >
              <InstagramIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
              <Typography variant="overline" sx={{
                color: 'text.disabled', fontSize: '0.62rem', flex: 1,
              }}>
                Instagram caption
              </Typography>
              <Typography sx={{
                fontSize: '0.68rem', color: 'text.disabled', fontWeight: 500,
              }}>
                {captionOpen ? 'Hide' : 'Show'}
              </Typography>
            </Box>
            {captionOpen && (
              <TextField
                value={instagramCaption}
                onChange={e => setInstagramCaption(e.target.value)}
                fullWidth multiline minRows={3} maxRows={10}
                variant="standard"
                sx={{ mt: 0.75 }}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.78rem', lineHeight: 1.55,
                    color: 'text.secondary',
                  },
                }}
              />
            )}
          </Box>
        )}

        {/* Visited — flat text toggles with underline-on-active */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
            Visited
          </Typography>
          {[
            { label: 'Not yet', val: false },
            { label: 'Been there', val: true },
          ].map(({ label, val }) => {
            const active = visited === val;
            return (
              <Box
                key={String(val)}
                onClick={() => setVisited(val)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '0.78rem', fontWeight: active ? 600 : 500,
                  letterSpacing: '-0.003em',
                  color: active ? (val ? 'primary.main' : 'text.primary') : 'text.disabled',
                  borderBottom: active ? '1px solid' : '1px solid transparent',
                  borderColor: active ? (val ? 'primary.main' : 'text.primary') : 'transparent',
                  pb: 0.1,
                  '&:hover': { color: active ? 'inherit' : 'text.secondary' },
                }}
              >
                {label}
              </Box>
            );
          })}
          {visited && (
            <Rating
              value={visitedRating}
              onChange={(_, v) => setVisitedRating(v)}
              size="small"
              sx={{ ml: 'auto', '& .MuiRating-iconFilled': { color: '#d9b26b' } }}
            />
          )}
        </Box>
        {visited && (
          <TextField
            value={visitedNotes}
            onChange={e => setVisitedNotes(e.target.value)}
            fullWidth multiline minRows={1} maxRows={4}
            variant="standard"
            placeholder="How was it? What'd you order?"
            InputProps={{
              sx: {
                fontSize: '0.82rem', letterSpacing: '-0.003em', lineHeight: 1.5,
                color: 'text.secondary', fontStyle: 'italic',
                '&:before': { borderBottom: '1px solid rgba(227,105,80,0.25) !important' },
                '&:after': { borderBottom: '1px solid rgba(227,105,80,0.6) !important' },
              },
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.25, pt: 0.5, gap: 0.5 }}>
        <Button onClick={onClose} size="small" sx={{ color: 'text.secondary', px: 1.5 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving}
          sx={{ px: 2.75, py: 0.75, color: '#1a1216' }}
        >
          {saving ? <CircularProgress size={14} color="inherit" /> : isEdit ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
